import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import { Box, Typography, MenuItem, TextField, Grid } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import MultiSelect from '@kenshooui/react-multi-select'

import helpers from 'helpers'
import useSnackbar from 'hooks/useSnackbar'
import useDataCollected from 'hooks/useDataCollected'
import styles from './styles'

const useStyles = makeStyles(styles)

const SharedCollectedFieldsFormModal = ({
  mode,
  sharedCollectedField,
  submitSuccess,
}) => {
  const classes = useStyles()
  const snackbar = useSnackbar()
  const { data: collectedDataOptions } = useDataCollected()

  const [selectedCategoryId, setSelectedCategoryId] = useState(null)
  const [selected, setSelected] = useState([])

  const isReadOnlyMode = mode === 'show'

  const multiSelectMessages = helpers.dataCollected.messagesMultiSelect()
  const filterFunction = (value) => (candidateItem) =>
    String(candidateItem.label).toLowerCase().includes(value.toLowerCase())
  const isMultiSelectDisabled = isReadOnlyMode || !selectedCategoryId

  useEffect(() => {
    if ((mode === 'edit' || mode === 'show') && sharedCollectedField) {
      setSelectedCategoryId(sharedCollectedField.titularCategory?.id)

      const initialSelected =
        sharedCollectedField.collectedFields?.map((collectedField) => ({
          id: collectedField.id,
          label: collectedField.dataCollectedOption?.name,
          group: helpers.dataCollected.dataTypeLabel(
            collectedField.dataCollectedOption?.dataType?.toString(),
          ),
        })) || []
      setSelected(initialSelected)
    }
  }, [mode, sharedCollectedField])

  const categoryOptions = []
  if (collectedDataOptions && !isEmpty(collectedDataOptions)) {
    const seenCategories = new Set()

    collectedDataOptions.forEach((dataOption) => {
      const id = dataOption.titularCategory?.id
      if (id && !seenCategories.has(id)) {
        seenCategories.add(id)
        categoryOptions.push({
          id,
          name: dataOption.titularCategory?.name,
        })
      }
    })
  }

  const availableData = (() => {
    const category = collectedDataOptions?.find(
      (dataOption) => dataOption.titularCategory?.id === selectedCategoryId,
    )

    return (
      category?.collectedFields?.map((field) => ({
        id: field.id,
        label: field.dataCollectedOption?.name,
        group: helpers.dataCollected.dataTypeLabel(
          field.dataCollectedOption?.dataType?.toString(),
        ),
      })) || []
    )
  })()

  const handleCategoryChange = (event) => {
    setSelectedCategoryId(event.target.value)
    setSelected([])
  }

  const onSubmit = async () => {
    if (mode === 'show') return

    if (!selectedCategoryId) {
      snackbar.open({
        message: 'Selecione uma categoria de titular',
        variant: 'warning',
      })
      return
    }

    if (isEmpty(selected)) {
      snackbar.open({
        message: 'Selecione pelo menos um dado para compartilhar',
        variant: 'warning',
      })
      return
    }

    const selectedCategory = categoryOptions.find(
      (cat) => cat.id === selectedCategoryId,
    )

    const selectedCategoryData = collectedDataOptions?.find(
      (dataOption) => dataOption.titularCategory?.id === selectedCategoryId,
    )

    const newField = {
      id: `temp-${Date.now()}`,
      titularCategory: {
        id: selectedCategoryId,
        name: selectedCategory?.name,
      },
      collectedFields: selected.map((field) => {
        const originalField = selectedCategoryData?.collectedFields?.find(
          (cf) => cf.id === field.id,
        )
        return {
          id: field.id,
          dataCollectedOption: {
            name: field.label,
            dataType: originalField?.dataCollectedOption?.dataType,
          },
        }
      }),
    }
    submitSuccess(newField)
  }

  const handleFormSubmit = (e) => {
    e.preventDefault()
    e.stopPropagation()
    onSubmit()
  }

  return (
    <form onSubmit={handleFormSubmit} id="shared-data-form">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box mt={1}>
            <TextField
              select
              label="Categoria do titular"
              fullWidth
              value={selectedCategoryId || ''}
              onChange={handleCategoryChange}
              disabled={isReadOnlyMode}
              variant="outlined"
            >
              {categoryOptions.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  <Typography>{category.name}</Typography>
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box mt={1}>
            <Typography variant="subtitle1" color="textSecondary">
              Dado(s) Compartilhado(s)
            </Typography>
          </Box>
          <MultiSelect
            messages={multiSelectMessages}
            items={availableData}
            selectedItems={selected}
            filterFunction={filterFunction}
            onChange={setSelected}
            disabled={isMultiSelectDisabled}
            withGrouping
            wrapperClassName={classes.multiSelect}
          />
        </Grid>
      </Grid>
    </form>
  )
}

SharedCollectedFieldsFormModal.propTypes = {
  mode: PropTypes.string,
  sharedCollectedField: PropTypes.object,
  submitSuccess: PropTypes.func,
}

export default SharedCollectedFieldsFormModal
